<template>
  <div class="payment-wrapper">
    <!-- QR Code View -->
    <Card v-if="!showVerificationForm" class="payment-card-wrapper">
      <template #title>
        <div class="payment-header">
          <h2 class="payment-title">{{ paymentDetails.description }}</h2>
          <p class="payment-subtitle">{{ paymentDetails.message }}</p>
        </div>
      </template>

      <template #content>
        <div class="payment-content">
          <!-- QR Code Section -->
          <div class="qr-section">
            <div class="qr-code-wrapper">
              <qrcode-vue :value="upiValue" :size="240" level="H" render-as="svg" class="qr-code" />
            </div>
            <p class="vpa-display">{{ vpa }}</p>
          </div>

          <!-- Payment Information -->
          <div class="payment-info">
            <div class="info-row">
              <span class="info-label">Payment Type:</span>
              <span class="info-value">{{ paymentDetails.description }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Amount:</span>
              <span class="info-value amount">₹{{ displayAmount }}</span>
            </div>
            <div class="info-row upi-id-row">
              <span class="info-label">UPI ID:</span>
              <div class="upi-id-value">
                <span class="info-value">{{ vpa }}</span>
                <button @click="copyUpiId" class="copy-upi-btn">
                  <i class="pi pi-copy"></i> Copy
                </button>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Reference:</span>
              <span class="info-value reference">{{ clientRef }}</span>
            </div>
          </div>

          <!-- Mobile Recommendation (Mobile Only) -->
          <div v-if="isMobile" class="mobile-recommendation">
            <i class="pi pi-info-circle"></i>
            <div>
              <strong>Recommended:</strong> Scan the QR code above with your UPI app for fastest payment. Direct app opening from browser may not always work.
            </div>
          </div>

          <!-- Mobile Payment Section -->
          <div class="mobile-payment-section">
            <p class="mobile-hint">{{ isMobile ? 'Fallback: Try opening app directly' : 'On mobile? Choose your payment app:' }}</p>
            <div class="payment-apps-grid fallback-apps">
              <a :href="getAppSpecificUpiLink('gpay')" class="app-pay-btn gpay">
                <span class="app-icon">G</span>
                <span>Google Pay</span>
              </a>
              <a :href="getAppSpecificUpiLink('phonepe')" class="app-pay-btn phonepe">
                <span class="app-icon">📱</span>
                <span>PhonePe</span>
              </a>
              <a :href="getAppSpecificUpiLink('paytm')" class="app-pay-btn paytm">
                <span class="app-icon">₹</span>
                <span>Paytm</span>
              </a>
              <a :href="upiValue" class="app-pay-btn default">
                <span class="app-icon">💳</span>
                <span>Other UPI</span>
              </a>
            </div>
            <p v-if="isMobile" class="warning-text">
              <i class="pi pi-exclamation-triangle"></i>
              If Paytm shows a risk-policy error, please use the QR code instead. QR payment is more reliable than browser-to-app redirection.
            </p>
          </div>

          <!-- Instructions -->
          <div class="payment-instructions">
            <h4>How to Pay:</h4>
            <ol>
              <li><strong>On Desktop:</strong> Scan the QR code above using any UPI app (GPay, PhonePe, Paytm, etc.)</li>
              <li><strong>On Mobile:</strong> {{ isMobile ? 'Scan QR code with your UPI app (recommended) or try app buttons above' : 'Click your preferred payment app button above to pay directly' }}</li>
              <li>Complete the payment in your UPI app</li>
              <li>Note down the UTR/Transaction Reference Number</li>
              <li>Click "I've Paid" below to verify your payment</li>
            </ol>
          </div>

          <!-- Action Buttons -->
          <div class="payment-actions">
            <ANButton
              v-if="!showVerificationForm"
              label="I've Paid - Verify Payment"
              variant="primary"
              icon="pi pi-check-circle"
              size="large"
              @click="markAsPaid"
              class="verify-btn"
            />
          </div>
        </div>
      </template>
    </Card>

    <!-- Verification Form View -->
    <Card v-if="showVerificationForm" class="verification-card-wrapper">
      <template #title>
        <div class="verification-header">
          <h2 class="verification-title">Verify Your Payment</h2>
          <p class="verification-subtitle">Enter your transaction details to complete verification</p>
        </div>
      </template>

      <template #content>
        <div class="verification-content">
          <!-- Success Message -->
          <div v-if="paymentSubmitted" class="success-message">
            <div class="success-icon-wrapper">
              <i class="pi pi-check-circle"></i>
            </div>
            <h3>Payment Details Submitted Successfully!</h3>
            <p>Our team will verify your payment within 24 hours.</p>
            <p class="info-text">You will receive an email confirmation once verified.</p>
          </div>

          <!-- Verification Form -->
          <form v-else @submit.prevent="handleSubmit" class="verification-form">
            <div class="form-group">
              <label for="utr">
                <i class="pi pi-tag label-icon"></i>
                UTR / Transaction Reference Number <span class="required">*</span>
              </label>
              <div class="input-wrapper">
                <i class="pi pi-hashtag input-icon"></i>
                <InputText
                  id="utr"
                  v-model="utr"
                  placeholder="Enter 12-digit UTR number"
                  :class="{ 'p-invalid': errors.utr }"
                  class="custom-input"
                />
              </div>
              <small v-if="errors.utr" class="p-error">
                <i class="pi pi-exclamation-circle"></i> {{ errors.utr }}
              </small>
              <small v-else class="helper-text">
                <i class="pi pi-info-circle"></i> Found in your payment app's transaction history
              </small>
            </div>

            <div class="form-group">
              <label for="amount">
                <i class="pi pi-indian-rupee label-icon"></i>
                Amount Paid <span class="required">*</span>
              </label>
              <div class="input-wrapper">
                <i class="pi pi-wallet input-icon"></i>
                <InputNumber
                  id="amount"
                  v-model="amount"
                  mode="currency"
                  currency="INR"
                  locale="en-IN"
                  class="custom-input"
                />
              </div>
              <small v-if="errors.amount" class="p-error">
                <i class="pi pi-exclamation-circle"></i> {{ errors.amount }}
              </small>
              <small v-else class="helper-text">
                <i class="pi pi-info-circle"></i> Enter the exact amount you paid
              </small>
            </div>

            <div class="form-actions">
              <ANButton
                type="submit"
                label="Submit for Verification"
                icon="pi pi-check"
                variant="primary"
                :loading="isSubmitting"
                class="submit-btn"
              />
              <ANButton
                type="button"
                label="Back to QR Code"
                icon="pi pi-arrow-left"
                variant="secondary"
                @click="showVerificationForm = false"
                class="back-btn"
              />
            </div>
          </form>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, reactive } from 'vue'
import { useUserStore } from '@/store/user-store'
import QrcodeVue from 'qrcode.vue'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import ANButton from '@/components/common/AnButton.vue'

const store = useUserStore()

// Device detection
const isMobile = ref(false)
const isIOS = ref(false)
const isAndroid = ref(false)

// Toggle between QR code and verification form
const showVerificationForm = ref(false)

// Verification form state
const utr = ref('')
const amount = ref(500)
const isSubmitting = ref(false)
const paymentSubmitted = ref(false)
const errors = reactive({ utr: '', amount: '' })

// Determine payment amount and subscription type based on current status
const paymentDetails = computed(() => {
  const subType = store.dashboardData?.subscriptionType
  const validThru = store.dashboardData?.validThruDate

  // Case 1: New user (no subscription type) - Trial payment
  if (!subType || subType === '') {
    return {
      amount: 500,
      subscriptionType: 'Trial',
      description: 'Trial Period (3 months)',
      message: 'Start your investment journey with our 3-month trial'
    }
  }

  // Case 2: Trial user with expired ValidThruDate - 9-month extension
  if (subType === 'Trial' && validThru) {
    const expiryDate = new Date(validThru)
    const today = new Date()

    if (expiryDate < today) {
      return {
        amount: 6000,
        subscriptionType: 'Annual',
        description: 'Annual Subscription (9 months remaining)',
        message: 'Continue your journey with our annual plan'
      }
    }
  }

  // Case 3: Annual user with expired ValidThruDate - Annual renewal
  if (subType === 'Annual' && validThru) {
    const expiryDate = new Date(validThru)
    const today = new Date()

    if (expiryDate < today) {
      return {
        amount: 12000,
        subscriptionType: 'Annual',
        description: 'Annual Renewal (12 months)',
        message: 'Renew your annual subscription'
      }
    }
  }

  // Default case - should not reach here if logic is correct
  return {
    amount: 500,
    subscriptionType: 'Trial',
    description: 'Payment',
    message: 'Complete your payment'
  }
})

// Set amount based on computed payment details
amount.value = paymentDetails.value.amount

// Payment Configuration
const vpa = "vivekgupta.ia@validau"
const payeeName = "VIVEK GUPTA"
const paymentAmount = computed(() => paymentDetails.value.amount)
const clientRef = computed(() => {
  const customerId = store.dashboardData?.customerId || 'NEW'
  return `AN-${customerId}-${new Date().getFullYear()}`
})

const displayAmount = computed(() => paymentAmount.value.toLocaleString('en-IN'))

// UPI Payment URI
const upiValue = computed(() => {
  return `upi://pay?pa=${vpa}&pn=${encodeURIComponent(payeeName)}&am=${paymentAmount.value.toFixed(2)}&tn=${encodeURIComponent(clientRef.value)}&cu=INR`
})

// Computed UPI link for manual use
const upiLink = computed(() => {
  return `upi://pay?pa=${vpa}&pn=${encodeURIComponent(payeeName)}&am=${paymentAmount.value.toFixed(2)}&tn=${encodeURIComponent('AssetNivesh-' + clientRef.value)}&cu=INR`
})

// App-specific UPI deep links
const getAppSpecificUpiLink = (app) => {
  const baseParams = `pa=${vpa}&pn=${encodeURIComponent(payeeName)}&am=${paymentAmount.value.toFixed(2)}&tn=${encodeURIComponent(clientRef.value)}&cu=INR`

  switch(app) {
    case 'gpay':
      return `tez://upi/pay?${baseParams}`
    case 'phonepe':
      return `phonepe://pay?${baseParams}`
    case 'paytm':
      return `paytmmp://pay?${baseParams}`
    default:
      return `upi://pay?${baseParams}`
  }
}

// Copy UPI ID to clipboard
const copyUpiId = async () => {
  try {
    await navigator.clipboard.writeText(vpa)
    store.fireAlert('Success', 'UPI ID copied to clipboard!')
  } catch (err) {
    store.fireAlert('Error', 'Failed to copy UPI ID')
  }
}

// Launch UPI app
const launchUpiApp = (appName) => {
  setTimeout(() => {
    window.location.href = upiLink.value
  }, 100)
}

// Mark as paid - show verification form
const markAsPaid = () => {
  showVerificationForm.value = true
}

// Verification form methods
const validateForm = () => {
  errors.utr = !utr.value || utr.value.length < 12 ? 'Enter valid 12-digit UTR' : ''
  errors.amount = !amount.value || amount.value <= 0 ? 'Enter valid amount' : ''
  return !errors.utr && !errors.amount
}

const handleSubmit = async () => {
  if (!validateForm()) return

  // Prevent duplicate submissions
  if (isSubmitting.value) {
    console.log('Submission already in progress')
    return
  }

  isSubmitting.value = true

  try {
    // Submit payment details with subscription type
    const response = await store.updateCustomerPaymentStatus({
      UTR: utr.value,
      Amount: amount.value,
      PaymentStatus: 'Paid',
      SubscriptionType: paymentDetails.value.subscriptionType
    })

    console.log('Payment status update response:', response)
    paymentSubmitted.value = true
    store.fireAlert('Success', 'Payment details submitted successfully! Awaiting verification.')

    // Navigate to payment status after a brief delay
    setTimeout(() => store.setTabView('PaymentStatus'), 2000)
  } catch (error) {
    store.fireAlert('Error', 'Failed to submit. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  document.title = 'Complete Payment - AssetNivesh'

  // Detect device type
  const ua = navigator.userAgent
  isMobile.value = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  isIOS.value = /iPhone|iPad|iPod/i.test(ua)
  isAndroid.value = /Android/i.test(ua)
})
</script>

<style scoped>
.payment-wrapper {
  padding: 24px;
  max-width: 700px;
  margin: 0 auto;
}

.payment-card-wrapper,
.verification-card-wrapper {
  box-shadow: var(--an-card-shadow);
}

.verification-header {
  text-align: center;
  padding-bottom: 8px;
}

.payment-title,
.verification-title {
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.3;
}

.payment-subtitle,
.verification-subtitle {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.5;
}

.payment-subtitle {
  color: #28a745;
  margin-bottom: 4px;
}

.verification-subtitle {
  color: #6c757d;
  margin-bottom: 8px;
}

.payment-content,
.verification-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* QR Code Section */
.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 2px solid #dee2e6;
}

.qr-code-wrapper {
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.qr-code {
  display: block;
}

.vpa-display {
  font-family: 'Courier New', monospace;
  color: #495057;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
}

/* Payment Information */
.payment-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid var(--an-primary);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  color: var(--an-text-secondary);
  font-weight: 500;
  font-size: 0.95rem;
}

.info-value {
  color: var(--an-text-primary);
  font-weight: 600;
  font-size: 1rem;
}

.info-value.amount {
  color: #28a745;
  font-size: 1.3rem;
}

.info-value.reference {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

/* UPI ID Copy Button */
.upi-id-row {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.upi-id-value {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.copy-upi-btn {
  padding: 6px 12px;
  background: var(--an-primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.copy-upi-btn:hover {
  background: #8e2a7d;
  transform: translateY(-1px);
}

.copy-upi-btn:active {
  transform: translateY(0);
}

/* Mobile Recommendation */
.mobile-recommendation {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #d1ecf1;
  border-left: 4px solid #0c5460;
  border-radius: 8px;
  color: #0c5460;
  font-size: 0.9rem;
  line-height: 1.5;
}

.mobile-recommendation i {
  font-size: 1.3rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.mobile-recommendation strong {
  color: #055160;
}

/* Warning Text */
.warning-text {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 12px;
  border-radius: 6px;
  color: #856404;
  font-size: 0.85rem;
  line-height: 1.5;
  margin-top: 8px;
  text-align: left;
}

.warning-text i {
  color: #ffc107;
  font-size: 1.1rem;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Fallback Apps Label */
.fallback-apps {
  opacity: 0.9;
}

/* Mobile Payment Section */
.mobile-payment-section {
  display: none; /* Hidden on desktop by default */
}

@media (max-width: 768px) {
  .mobile-payment-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 24px;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border-radius: 8px;
  }
}

.mobile-hint {
  color: #1976d2;
  font-weight: 600;
  margin: 0;
  font-size: 0.95rem;
}

.payment-apps-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;
  max-width: 400px;
}

.app-pay-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.app-pay-btn .app-icon {
  font-size: 1.8rem;
  font-weight: 700;
}

.app-pay-btn.gpay {
  background: linear-gradient(135deg, #4285f4 0%, #34a853 100%);
  color: white;
}

.app-pay-btn.phonepe {
  background: linear-gradient(135deg, #5f259f 0%, #3e1c69 100%);
  color: white;
}

.app-pay-btn.paytm {
  background: linear-gradient(135deg, #00baf2 0%, #0a7aaf 100%);
  color: white;
}

.app-pay-btn.default {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: white;
}

.app-pay-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.app-pay-btn:active {
  transform: translateY(0);
}

/* Instructions */
.payment-instructions {
  padding: 20px;
  background: #fff3cd;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
}

.payment-instructions h4 {
  color: #856404;
  margin: 0 0 12px 0;
  font-size: 1.1rem;
}

.payment-instructions ol {
  margin: 0;
  padding-left: 20px;
  color: #856404;
}

.payment-instructions li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.payment-instructions strong {
  color: #533f03;
}

/* Action Buttons */
.payment-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.verify-btn {
  width: 100%;
}

/* Verification Form Styles */
.verification-content {
  gap: 24px;
}

.verification-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 550px;
  margin: 0 auto;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.label-icon {
  color: #667eea;
  font-size: 1.15rem;
}

.required {
  color: #e74c3c;
  font-weight: 700;
  margin-left: 2px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 18px;
  color: #95a5a6;
  font-size: 1.15rem;
  z-index: 1;
}

.form-group :deep(.p-inputtext),
.form-group :deep(.p-inputnumber-input) {
  padding: 16px 18px 16px 52px;
  border: 2px solid #e0e3e7;
  border-radius: 10px;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  width: 100%;
  background: #fafbfc;
  font-weight: 500;
}

.form-group :deep(.p-inputtext:hover),
.form-group :deep(.p-inputnumber-input:hover) {
  border-color: #cbd5e0;
  background: white;
}

.form-group :deep(.p-inputtext:focus),
.form-group :deep(.p-inputnumber-input:focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.12);
  outline: none;
  background: white;
}

.form-group :deep(.p-invalid) {
  border-color: #e74c3c;
  background: #fff5f5;
}

.form-group :deep(.p-error) {
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.helper-text {
  color: #6c757d;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.helper-text i {
  color: #3498db;
  font-size: 0.95rem;
}

.form-actions {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.form-actions :deep(button) {
  flex: 1;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  min-height: 44px;
}

.form-actions :deep(.submit-btn) {
  background: var(--an-primary);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-actions :deep(.submit-btn:hover:not(:disabled)) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.form-actions :deep(.back-btn) {
  background: white;
  color: #28a745;
  border: 2px solid #28a745;
}

.form-actions :deep(.back-btn:hover) {
  background: #28a745;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.success-message {
  text-align: center;
  padding: 56px 40px;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-radius: 12px;
  border: 2px solid #28a745;
  max-width: 550px;
  margin: 0 auto;
}

.success-icon-wrapper {
  width: 90px;
  height: 90px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 4px 20px rgba(40, 167, 69, 0.25);
}

.success-message i {
  font-size: 4rem;
  color: #28a745;
  animation: checkmark-pop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes checkmark-pop {
  0% { transform: scale(0) rotate(0deg); }
  50% { transform: scale(1.2) rotate(10deg); }
  100% { transform: scale(1) rotate(0deg); }
}

.success-message h3 {
  color: #155724;
  margin: 0 0 16px 0;
  font-size: 1.6rem;
  font-weight: 700;
}

.success-message p {
  color: #155724;
  font-size: 1.05rem;
  margin: 0 0 10px 0;
  font-weight: 500;
  line-height: 1.5;
}

.info-text {
  color: #6c757d !important;
  font-size: 0.95rem !important;
  font-weight: 400 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .payment-wrapper {
    padding: 12px;
  }

  .payment-title,
  .verification-title {
    font-size: 1.4rem;
    margin: 0 0 12px 0;
  }

  .payment-subtitle,
  .verification-subtitle {
    font-size: 0.85rem;
    margin-bottom: 6px;
  }

  .qr-code-wrapper :deep(svg) {
    width: 200px !important;
    height: 200px !important;
  }

  .payment-instructions {
    font-size: 0.85rem;
    padding: 16px;
  }

  .verification-form {
    max-width: 100%;
    gap: 24px;
  }

  .form-group {
    gap: 10px;
  }

  .form-group label {
    font-size: 0.95rem;
    gap: 8px;
  }

  .label-icon {
    font-size: 1rem;
  }

  .input-icon {
    left: 14px;
    font-size: 1rem;
  }

  .form-group :deep(.p-inputtext),
  .form-group :deep(.p-inputnumber-input) {
    padding: 14px 14px 14px 46px;
    font-size: 0.95rem;
    border-radius: 8px;
  }

  .form-group :deep(.p-error),
  .helper-text {
    font-size: 0.8rem;
  }

  .form-actions {
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
  }

  .form-actions :deep(button) {
    width: 100%;
    padding: 12px 20px;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .success-message {
    padding: 40px 24px;
  }

  .success-icon-wrapper {
    width: 70px;
    height: 70px;
  }

  .success-message i {
    font-size: 3rem;
  }

  .success-message h3 {
    font-size: 1.3rem;
  }

  .success-message p {
    font-size: 0.95rem;
  }

  .info-text {
    font-size: 0.85rem !important;
  }
}

@media (max-width: 480px) {
  .payment-wrapper {
    padding: 8px;
  }

  .payment-title,
  .verification-title {
    font-size: 1.25rem;
    margin: 0 0 10px 0;
  }

  .payment-subtitle,
  .verification-subtitle {
    font-size: 0.8rem;
    margin-bottom: 4px;
  }

  .verification-form {
    gap: 20px;
  }

  .form-group {
    gap: 8px;
  }

  .form-group label {
    font-size: 0.9rem;
    flex-wrap: wrap;
  }

  .form-group :deep(.p-inputtext),
  .form-group :deep(.p-inputnumber-input) {
    padding: 12px 12px 12px 42px;
    font-size: 0.9rem;
  }

  .form-actions :deep(button) {
    padding: 11px 16px;
    font-size: 0.9rem;
    min-height: 42px;
  }

  .success-message {
    padding: 32px 20px;
  }

  .success-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .success-message i {
    font-size: 2.5rem;
  }

  .success-message h3 {
    font-size: 1.2rem;
  }

  .success-message p {
    font-size: 0.9rem;
  }
}

/* Tablet landscape */
@media (min-width: 769px) and (max-width: 1024px) {
  .payment-wrapper {
    max-width: 650px;
  }

  .verification-form {
    max-width: 500px;
  }

  .form-group label {
    font-size: 1rem;
  }

  .form-group :deep(.p-inputtext),
  .form-group :deep(.p-inputnumber-input) {
    padding: 14px 16px 14px 50px;
    font-size: 1rem;
  }

  .form-actions :deep(button) {
    padding: 13px 24px;
    font-size: 1rem;
    min-height: 46px;
  }
}
</style>
